import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface NewsArticle {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  imageAlt: string;
  author: string;
  tags: string[];
  featured: boolean;
}

interface NewsCardProps {
  article: NewsArticle;
  onReadMore: (id: string) => void;
}

export default function NewsCard({ article, onReadMore }: NewsCardProps) {
  return (
    <article className="bg-card rounded-lg shadow-organic hover:shadow-organic-sm transition-organic duration-300 overflow-hidden border border-border group">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={article.image}
          alt={article.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-organic duration-500"
        />
        {article.featured && (
          <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-cta font-semibold flex items-center space-x-1">
            <Icon name="StarIcon" size={14} variant="solid" />
            <span>Featured</span>
          </div>
        )}
        <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-body font-medium">
          {article.category}
        </div>
      </div>
      
      <div className="p-6 space-y-4">
        <div className="flex items-center space-x-4 text-sm text-muted-foreground font-body">
          <div className="flex items-center space-x-1">
            <Icon name="CalendarIcon" size={16} variant="outline" />
            <span>{article.date}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="ClockIcon" size={16} variant="outline" />
            <span>{article.readTime}</span>
          </div>
        </div>
        
        <h3 className="text-xl font-headline font-bold text-foreground line-clamp-2 group-hover:text-primary transition-organic duration-300">
          {article.title}
        </h3>
        
        <p className="text-muted-foreground font-body line-clamp-3 leading-relaxed">
          {article.excerpt}
        </p>
        
        <div className="flex flex-wrap gap-2">
          {article.tags.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-muted text-muted-foreground text-xs font-body rounded-md"
            >
              #{tag}
            </span>
          ))}
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <Icon name="UserIcon" size={16} variant="solid" className="text-primary" />
            </div>
            <span className="text-sm font-body text-foreground">{article.author}</span>
          </div>
          
          <button
            onClick={() => onReadMore(article.id)}
            className="flex items-center space-x-2 text-primary hover:text-primary/80 font-cta text-sm font-semibold transition-organic duration-300"
          >
            <span>Read More</span>
            <Icon name="ArrowRightIcon" size={16} variant="outline" />
          </button>
        </div>
      </div>
    </article>
  );
}